import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, Download } from 'lucide-react';

import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { ICON_STROKE_WIDTH } from '../../constants';
import {
  BillingAnalyticsBreakdownItem,
  BillingAnalyticsReconciliation,
  getBillingReconciliation,
} from '../../services/billingAnalyticsService';
import {
  BillingExportFormat,
  exportReconciliationReport,
  formatBillingAxisCurrency,
  formatBillingCompactCurrency,
  formatCurrencyInr,
  formatDisplayDate,
  formatPeriodLabel,
  localTodayIso,
} from '../../utils/billingReconciliationExport';
import { computeNiceTicks } from '../../components/analytics/chartUtils';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './BillingReconciliation.css';
import '../../components/analytics/analytics-theme.css';

const BILLING_CHART_PLOT_HEIGHT = 150;

function billingSummaryCardValue(
  card: { value: number; isCount?: boolean },
  loading: boolean
): string {
  if (loading) {
    return '—';
  }
  if (card.isCount) {
    return String(card.value);
  }
  return formatCurrencyInr(card.value);
}

function BillingReconDateField({
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayText = value ? formatDisplayDate(value) : label;

  return (
    <div className="billing-recon-date-field">
      <span className="billing-recon-date-field__caption">{label}</span>
      <label className="billing-recon-date-field__control">
        <input
          ref={inputRef}
          type="date"
          className="billing-recon-date-field__input"
          aria-label={label}
          value={value}
          min={min}
          max={max}
          onChange={event => onChange(event.target.value)}
        />
        <div
          className="billing-recon-date-field__face"
          aria-hidden
        >
          <span className="billing-recon-date-field__value">{displayText}</span>
          <Calendar
            className="billing-recon-date-field__icon"
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </div>
      </label>
    </div>
  );
}

function BarChartPanel({
  title,
  subtitle,
  items,
}: Readonly<{
  title: string;
  subtitle: string;
  items: BillingAnalyticsBreakdownItem[];
}>) {
  const dataMax = useMemo(() => Math.max(...items.map(item => item.amount), 0), [items]);
  const yTicks = useMemo(() => computeNiceTicks(dataMax, 4), [dataMax]);
  const tickMax = yTicks[0] || Math.max(dataMax, 1);
  const isMobileChart = useMediaQuery('(max-width: 767px)');
  const plotMinWidth = useMemo(() => {
    if (isMobileChart) {
      return Math.max(items.length * 56, 100);
    }
    return Math.max(items.length * 88, 280);
  }, [isMobileChart, items.length]);

  return (
    <section className="billing-chart-card">
      <div className="billing-chart-header">
        <h3 className="billing-chart-title">{title}</h3>
        <p className="billing-chart-subtitle">{subtitle}</p>
      </div>
      {items.length === 0 ? (
        <p className="billing-recon-empty">No transactions found</p>
      ) : (
        <div className="billing-chart-body">
          <div className="billing-chart-y-wrap">
            <div
              className="billing-chart-y-axis"
              aria-hidden
            >
              {yTicks.map(tick => (
                <span
                  key={`${title}-tick-${tick}`}
                  className="billing-chart-y-tick"
                >
                  {formatBillingAxisCurrency(tick)}
                </span>
              ))}
            </div>
            <div
              className="billing-chart-y-spacer"
              aria-hidden
            />
          </div>
          <div className="billing-chart-plot-scroll">
            <div
              className="billing-chart-plot"
              style={{ minWidth: plotMinWidth }}
            >
              <div className="billing-chart-plot-main">
                <div
                  className="billing-chart-grid"
                  aria-hidden
                >
                  {yTicks.map(tick => (
                    <div
                      key={`${title}-grid-${tick}`}
                      className="billing-chart-grid-line"
                    />
                  ))}
                </div>
                <div className="billing-chart-cols">
                  {items.map(item => {
                    const barHeight =
                      item.amount > 0 && tickMax > 0
                        ? Math.max(
                            4,
                            Math.round((item.amount / tickMax) * BILLING_CHART_PLOT_HEIGHT)
                          )
                        : 0;
                    return (
                      <div
                        key={`${title}-${item.label}`}
                        className="billing-chart-col"
                      >
                        <div className="billing-chart-col-plot">
                          <div className="billing-chart-bar-track">
                            <div className="billing-chart-bar-inner">
                              <span className="billing-chart-bar-value">
                                {formatBillingCompactCurrency(item.amount)}
                              </span>
                              <div
                                className="billing-chart-bar-fill"
                                style={{ height: `${barHeight}px` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="billing-chart-x-label-wrap">
                          <span
                            className="billing-chart-x-label"
                            title={item.label}
                          >
                            {item.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function BillingReconciliation() {
  const [startDate, setStartDate] = useState(localTodayIso());
  const [endDate, setEndDate] = useState(localTodayIso());
  const [report, setReport] = useState<BillingAnalyticsReconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const dashboardUser = authService.getUser();
  const location = useLocation();
  const isAnalyticsRoute = location.pathname.startsWith('/analytics/');
  const facilityName =
    typeof dashboardUser?.facility === 'object' && dashboardUser?.facility?.facility_name
      ? String(dashboardUser.facility.facility_name)
      : undefined;

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getBillingReconciliation({ startDate, endDate });
    if (res.success && res.data) {
      setReport(res.data);
    } else {
      setReport(null);
      setError(res.error || 'Failed to load report');
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    if (!exportMenuOpen) {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  const handleExport = async (format: BillingExportFormat) => {
    setExportMenuOpen(false);
    if (!report) {
      setError('No report data to export. Adjust filters and try again.');
      return;
    }

    try {
      setExporting(true);
      setError(null);
      exportReconciliationReport(format, report, {
        startDate,
        endDate,
        generatedBy: dashboardUser?.name || dashboardUser?.username || dashboardUser?.email,
        facilityName,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = [
    { title: 'Total collection', value: report?.summary.totalCollection ?? 0 },
    { title: 'Refunds', value: report?.summary.refunds ?? 0 },
    { title: 'Discounts', value: report?.summary.discounts ?? 0 },
    { title: 'Net collection', value: report?.summary.netCollection ?? 0, highlight: true },
    { title: 'Transactions', value: report?.summary.transactionCount ?? 0, isCount: true },
  ];

  if (
    !canAccessPage(
      dashboardUser,
      isAnalyticsRoute ? '/analytics/collection-reconciliation' : '/billing-reconciliation'
    )
  ) {
    return (
      <Navigate
        to={getDefaultRoute(dashboardUser)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="Collection Reconciliation"
      hideTitleInHeader
      contentClassName="billing-recon-page-layout-content"
      breadcrumbs={
        isAnalyticsRoute
          ? [
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Admin' },
              { label: 'Analytics' },
              { label: 'Collection reconciliation' },
            ]
          : [
              { label: 'Home', path: '/' },
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Collection Reconciliation' },
            ]
      }
    >
      <div
        className={
          isAnalyticsRoute ? 'analytics-page billing-reconciliation' : 'billing-reconciliation'
        }
      >
        <header className="billing-recon-header">
          <div>
            <h1 className="billing-recon-title">Collection reconciliation</h1>
            <p className="billing-recon-period-label">
              Showing data for <strong>{formatPeriodLabel(startDate, endDate)}</strong>
            </p>
          </div>
          <div className="billing-recon-actions">
            <div className="billing-recon-period">
              <BillingReconDateField
                label="From"
                value={startDate}
                max={endDate}
                onChange={setStartDate}
              />
              <BillingReconDateField
                label="To"
                value={endDate}
                min={startDate}
                max={localTodayIso()}
                onChange={setEndDate}
              />
            </div>
            <div
              className="billing-recon-export-wrap"
              ref={exportMenuRef}
            >
              <button
                type="button"
                className="billing-recon-export-btn"
                onClick={() => setExportMenuOpen(open => !open)}
                disabled={exporting || loading || !report}
                aria-haspopup="menu"
                aria-expanded={exportMenuOpen}
              >
                <Download
                  strokeWidth={ICON_STROKE_WIDTH}
                  size={16}
                />
                Export
                <ChevronDown
                  strokeWidth={ICON_STROKE_WIDTH}
                  size={16}
                />
              </button>
              {exportMenuOpen && (
                <div
                  className="billing-recon-export-menu"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport('pdf')}
                  >
                    Export as PDF
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport('csv')}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleExport('excel')}
                  >
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {error && (
          <p
            className="billing-recon-error"
            role="alert"
          >
            {error}
          </p>
        )}

        <section className="billing-recon-summary">
          {summaryCards.map(card => (
            <div
              key={card.title}
              className={['billing-recon-summary-card', card.highlight ? 'highlight' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <span className="billing-recon-summary-label">{card.title}</span>
              <span className="billing-recon-summary-value">
                {billingSummaryCardValue(card, loading)}
              </span>
            </div>
          ))}
        </section>

        {!loading && report && (
          <p className="billing-recon-filter-note">
            Filter: {formatDisplayDate(startDate)} – {formatDisplayDate(endDate)} · Completed
            payments only
          </p>
        )}

        <div className="billing-recon-charts-grid">
          <BarChartPanel
            title="Collection by users"
            subtitle="Top collectors in the selected period"
            items={report?.byUser || []}
          />
          <BarChartPanel
            title="Collection by payment mode"
            subtitle="Breakdown by payment method"
            items={report?.byPaymentMode || []}
          />
          <BarChartPanel
            title="Collection by schemes"
            subtitle="Breakdown by payer / scheme type"
            items={report?.byScheme || []}
          />
          <BarChartPanel
            title="Collection by counters"
            subtitle="Breakdown by billing counter"
            items={report?.byCounter || []}
          />
        </div>
      </div>
    </PageLayout>
  );
}

export default BillingReconciliation;
